import React, { useState, useEffect, useMemo } from 'react';
import ActionModal, { ActionModalFooter } from 'common-components/modal/ActionModal';
import { Avatar, Col, Empty, Icon, Skeleton } from 'antd';
import { FieldLabel, Text } from 'common-components/typography';
import { FilterSection, YearAndMonthFilter } from 'common-components/filter';
import moment from 'moment-timezone';
import _ from 'lodash';
import { IGroupServiceTeamMember } from 'interfaces/service-interfaces';
import { PrimaryButton } from 'common-components/buttons';
import { GridHeader, GridRow } from 'common-components/grids';
import * as H from 'history';
import { BookingTypeIcon, StatusTag } from 'common-components/tags';
import { useFetchTeamMemberSchedule } from 'stores/hooks/query-hooks/use-query-fetch-team-member-schedule';
import { FilterType, ServiceType } from 'utilities/enum-utils';
import CommonUtils from 'utilities/common-utils';

interface IFilter {
  year: number;
  month: number;
}

function ViewTeamMemberScheduleModal({
  onClose,
  selectedTeamMember,
  selectedGroupService,
}: {
  onClose: () => void;
  selectedTeamMember: IGroupServiceTeamMember;
  selectedGroupService: any;
}) {
  // Initialize with current year/month
  const now = moment();
  const [filter, setFilter] = useState<IFilter>({ year: now.year(), month: now.month() });
  const [filteredShiftsList, setFilteredShiftsList] = useState<any>([]);
  const [filterUpdateCounter, setFilterUpdateCounter] = useState(0);
  const [isInitialized, setIsInitialized] = useState(false);
  const availableFilters = [FilterType.SHIFT_SLOT_STATUS];

  const defaultFilters = [
    {
      filter: FilterType.SHIFT_SLOT_STATUS,
      values: [],
      selectionLabel: CommonUtils.getFilterSettings(FilterType.SHIFT_SLOT_STATUS).fullSelectionName,
    },
  ];
  const [activeFilters, setActiveFilters] = useState<any>(defaultFilters);

  const afterFetch = () => {
    // Filtering is now handled by useEffect
    // This callback is kept for compatibility with the hook
  };

  const { data, isLoading } = useFetchTeamMemberSchedule(
    {
      serviceIds: [selectedGroupService.serviceId],
      supportWorkerId: selectedTeamMember?.supportWorkerId,
      shiftSlotStatuses: _.find(activeFilters, (activeFilter) => activeFilter.filter === FilterType.SHIFT_SLOT_STATUS)
        .values,
    },
    true, // Always enable the query
    afterFetch,
  );

  const shifts = data
    ? _.sortBy(data?.data.shifts, (shift) => shift.shiftStartDateTime || shift.sessionStartDateTime)
    : [];

  // Calculate the date range from all shifts for the filter component
  const dateRange = useMemo(() => {
    if (shifts.length === 0) {
      return { start: moment(), end: moment() };
    }

    const dates = shifts.map((shift) => moment(shift.shiftStartDateTime || shift.sessionStartDateTime));
    const minDate = moment.min(dates);
    const maxDate = moment.max(dates);

    return { start: minDate, end: maxDate };
  }, [shifts]);

  // Initialize filter to closest month with data when shifts first load
  useEffect(() => {
    if (shifts.length > 0 && !isInitialized) {
      const currentNow = moment();
      const currentMonth = currentNow.month();
      const currentYear = currentNow.year();

      // Check if current month has data
      const currentMonthShifts = shifts.filter((shift) => {
        const shiftDateTime = shift.shiftStartDateTime || shift.sessionStartDateTime;
        return moment(shiftDateTime).year() === currentYear && moment(shiftDateTime).month() === currentMonth;
      });

      // If current month has data, keep it
      if (currentMonthShifts.length > 0) {
        setIsInitialized(true);
        return;
      }

      // Otherwise, find the closest month with data
      let closestDate = null;
      let minDiff = Number.POSITIVE_INFINITY;

      for (const shift of shifts) {
        const shiftDateTime = shift.shiftStartDateTime || shift.sessionStartDateTime;
        const shiftDate = moment(shiftDateTime);
        const diff = Math.abs(shiftDate.diff(currentNow));

        if (diff < minDiff) {
          minDiff = diff;
          closestDate = shiftDate;
        }
      }

      if (closestDate) {
        setFilter({
          year: closestDate.year(),
          month: closestDate.month(),
        });
      }

      setIsInitialized(true);
    }
  }, [shifts, isInitialized]);

  // Update filtered shifts whenever shifts data or filter changes
  useEffect(() => {
    if (shifts.length > 0) {
      setFilteredShiftsList(
        _.filter(shifts, (shift) => {
          const shiftDateTime = shift.shiftStartDateTime || shift.sessionStartDateTime;
          return moment(shiftDateTime).year() === filter.year && moment(shiftDateTime).month() === filter.month;
        }),
      );
    }
  }, [shifts, filter.year, filter.month]);

  // Memoize the dateList to prevent unnecessary re-renders
  const dateList = useMemo(() => {
    return _.map(shifts, (shift) => {
      return {
        ...shift,
        startDateTime: shift.shiftStartDateTime || shift.sessionStartDateTime,
        endDateTime: shift.shiftEndDateTime || shift.sessionEndDateTime,
      };
    });
  }, [shifts]);

  const _updateFilter = (newFilter) => {
    // Create a new object reference to ensure PureComponent detects the change
    setFilter({ ...newFilter });
    // Increment counter to force re-render of YearAndMonthFilter component
    setFilterUpdateCounter((prev) => prev + 1);
  };

  const _onChangeFilter = (e) => {
    setActiveFilters(e);
    // Reset to current year/month when filters change
    const now = moment();
    setFilter({
      year: now.year(),
      month: now.month(),
    });
  };

  const TeamMemberFullName = selectedTeamMember
    ? `${selectedTeamMember.teamMemberFirstName} ${selectedTeamMember.teamMemberLastName}`
    : '';

  const SessionEmptyState = () => (
    <div className='flex-1 bg-white mt-x2-large align-center flex-column'>
      <div className=''>
        <Empty description={false} image={Empty.PRESENTED_IMAGE_SIMPLE} className='mv-none' />
      </div>
      <Text size='x2-large' color='secondary' weight='bold'>
        No session found for the selected month.
      </Text>
    </div>
  );

  return (
    <ActionModal width='x2-large' isOpen={true} onClose={onClose} title={'View team member schedule'}>
      {isLoading ? (
        <Skeleton paragraph={{ rows: 3, width: '100%' }} active={true} className='anim-slide-left' />
      ) : (
        <>
          {' '}
          <Text>You’re currently viewing the schedule for this following team member:</Text>
          <div className='flex-row justify-between'>
            <div className='flex-row align-center'>
              <Avatar
                size='large'
                className='mr-small'
                icon='user'
                src={selectedTeamMember && selectedTeamMember.teamMemberAvatarUrl}
              />
              <Text weight='bold'>{TeamMemberFullName}</Text>
            </div>
            <div className='flex-row align-center' style={{ marginTop: '1px' }}>
              <div className='mr-small'>
                <Icon type={'filter'} className={'text-color-tertiary'} />
              </div>
              <div style={{ marginTop: '10px' }}>
                <FilterSection
                  availableFilters={availableFilters}
                  filters={activeFilters}
                  onChangeFilter={_onChangeFilter}
                  displayTimezone={null}
                  displayMoreFilter={false}
                  usePortal={false}
                />
              </div>
            </div>
          </div>
          <YearAndMonthFilter
            key={filterUpdateCounter}
            filter={filter}
            startDateTime={dateRange.start}
            endDateTime={dateRange.end}
            updateFilter={_updateFilter}
            hideEmptyDates={true}
            dateList={dateList}
          />
          <GridHeader bordered containerClassName='border-width-medium border-secondary'>
            <Col span={6} className=' bg-white'>
              <FieldLabel text='SHIFT TIME' />
            </Col>
            <Col span={8} className=' bg-white'>
              <FieldLabel text={'SERVICE'} />
            </Col>
            <Col span={4} className=' bg-white'>
              <FieldLabel text={'STATUS'} />
            </Col>
          </GridHeader>
          {filteredShiftsList.length === 0 ? (
            <SessionEmptyState />
          ) : (
            _.map(filteredShiftsList, (shift) => {
              const shiftDateTime = shift.shiftStartDateTime || shift.sessionStartDateTime;
              const endDateTime = shift.shiftEndDateTime || shift.sessionEndDateTime;
              const date = moment.tz(shiftDateTime, selectedGroupService.timezone);
              const startDate = moment.tz(shiftDateTime, selectedGroupService.timezone);
              const endTime = moment.tz(endDateTime, selectedGroupService.timezone);
              return (
                <GridRow className=''>
                  <Col span={6}>
                    <Text>{date.format('ddd, D MMM YYYY')}</Text>
                    <br />
                    <Text type='secondary'>
                      {startDate.format('HH:mm a')} - {endTime.format('HH:mm a')}
                    </Text>
                  </Col>
                  <Col span={8}>
                    {shift.serviceType === ServiceType.GROUP ? (
                      <>
                        <div className='flex-row align-center'>
                          <div>
                            <BookingTypeIcon type={shift.serviceType} className={'mr-small'} />
                          </div>
                          <div>
                            <div className='mr-0'>
                              <Text>{shift.serviceName}</Text>
                            </div>
                            <div>
                              <Text type='secondary'>{`${moment
                                .tz(shift.sessionStartDateTime, shift.timezone)
                                .format('Do MMM YYYY')}, ${moment
                                .tz(shift.sessionStartDateTime, shift.timezone)
                                .format('HH:mm a')} - ${moment
                                .tz(shift.sessionEndDateTime, shift.timezone)
                                .format('HH:mm a')}`}</Text>
                            </div>
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className='flex-row align-center'>
                          <div>
                            <BookingTypeIcon type={shift.serviceType} className={'mr-small'} />
                          </div>
                          <div className='mr-0'>
                            <Text>{shift.serviceName}</Text>
                          </div>
                        </div>
                      </>
                    )}
                  </Col>
                  <Col span={4}>
                    <StatusTag status={shift.shiftSlotStatus} size='small' />
                  </Col>
                </GridRow>
              );
            })
          )}
          <ActionModalFooter className='mt-medium'>
            <PrimaryButton size='large' onClick={onClose}>
              Close
            </PrimaryButton>
          </ActionModalFooter>
        </>
      )}
    </ActionModal>
  );
}

export default ViewTeamMemberScheduleModal;
